import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Download, FileText, Shield, Cpu, Wrench } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ImageGallery } from '@/components/products/ImageGallery'
import { ProductCard } from '@/components/products/ProductCard'
import { getProductBySlug, getProductsByCategory, productCategories } from '@/data/products'
import { NotFoundPage } from './NotFoundPage'
import type { SupportedLanguage } from '@/i18n'

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true } as const,
}

export function ProductDetailPage() {
  const { productSlug } = useParams<{ productSlug: string }>()
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage

  const product = productSlug ? getProductBySlug(productSlug) : undefined
  const category = product ? productCategories.find((c) => c.id === product.category) : undefined

  if (!product || !category) return <NotFoundPage />

  const relatedProducts = getProductsByCategory(product.category)
    .filter((p) => p.id !== product.id)
    .slice(0, 3)

  const subtitle = t(product.subtitleKey)
  const fullDescription = t(product.fullDescriptionKey)
  const applications = t(product.applicationsKey)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  const contactPath = lang === 'en' ? '/contacts' : '/contatti'
  const productsPath = lang === 'en' ? 'products' : 'prodotti'

  // Highlight pills: first 3-4 features (title only)
  const highlightFeatures = product.features.slice(0, 4)
  const hasVideo = Boolean(product.videoUrl)
  const hasDownloads = product.downloads.length > 0
  const isManyFeatures = product.features.length >= 6

  return (
    <>
      <SEOHead
        titleKey={product.subtitleKey}
        descriptionKey={product.descriptionKey}
      />

      {/* ── 1. Hero Cinematografico ── */}
      <section className="relative bg-primary overflow-hidden min-h-[75vh] flex items-center">
        {/* Decorative elements */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
        {/* Ghost watermark */}
        <div className="absolute bottom-8 right-8 text-white/[0.02] text-[8rem] md:text-[12rem] font-black leading-none pointer-events-none select-none whitespace-nowrap">
          {product.name}
        </div>

        <div className="container-main relative z-10 section-padding">
          {/* Breadcrumb */}
          <nav className="text-sm text-white/50 mb-8" aria-label="Breadcrumb">
            <LocalizedLink to="home" className="hover:text-white/80 transition-colors">
              Home
            </LocalizedLink>
            <span className="mx-2">/</span>
            <LocalizedLink
              to={`/${productsPath}`}
              className="hover:text-white/80 transition-colors"
            >
              {t('nav.products')}
            </LocalizedLink>
            <span className="mx-2">/</span>
            <span className="text-white/80">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Info — 5 col */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5 flex flex-col"
            >
              <div className="inline-block self-start px-3 py-1 bg-accent text-white text-xs font-semibold uppercase tracking-wider rounded mb-5">
                {t(category.nameKey)}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
                {product.name}
              </h1>
              <h2 className="text-lg md:text-xl text-white/70 mb-4">{subtitle}</h2>
              <p className="text-white/60 leading-relaxed mb-6">
                {t(product.descriptionKey)}
              </p>

              {/* Highlight pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                {highlightFeatures.map((feat, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-white/80 text-xs font-medium"
                  >
                    {t(feat.titleKey)}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-wrap gap-4">
                <LocalizedLink
                  to={contactPath}
                  className="btn-primary"
                >
                  {t('buttons.contactUs')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </LocalizedLink>
                {hasDownloads && (
                  <a
                    href={product.downloads[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline border-white text-white hover:bg-white hover:text-primary"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t(product.downloads[0].labelKey)}
                  </a>
                )}
              </div>
            </motion.div>

            {/* Gallery — 7 col */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <ImageGallery images={product.images} alt={product.name} variant="dark" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. Descrizione Completa ── */}
      <section className="bg-white section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-text-heading mb-4"
            >
              {t('products.detail.sections.fullDescription')}
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-16 h-1 bg-accent mx-auto mb-6 origin-center"
            />
            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-text-body text-lg leading-relaxed"
            >
              {fullDescription}
            </motion.p>
          </div>
        </div>
      </section>

      {/* ── 3. Caratteristiche — Bento Grid ── */}
      <section className="relative bg-primary overflow-hidden section-padding">
        {/* Decorative */}
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-main relative z-10">
          {/* Eyebrow + heading */}
          <motion.div {...fadeInUp} transition={{ duration: 0.5 }} className="text-center mb-12">
            <span className="text-accent text-sm font-semibold uppercase tracking-wider">
              {t('products.detail.sections.featuresEyebrow')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-3">
              {t('products.detail.sections.features')}
            </h2>
          </motion.div>

          {isManyFeatures ? (
            /* 6+ features → Bento grid 3 cols, first card hero (col-span-2) */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {product.features.map((feature, index) => {
                const isHero = index === 0
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.06 }}
                    className={`group relative rounded-2xl bg-white/5 border border-white/10 p-6 md:p-8 backdrop-blur-sm overflow-hidden hover:border-accent/30 transition-colors ${
                      isHero ? 'md:col-span-2' : ''
                    }`}
                  >
                    {/* Ghost number */}
                    <span className="absolute top-4 right-5 text-[4rem] font-black text-white/[0.04] leading-none pointer-events-none select-none">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {/* Hover accent bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

                    <h3 className="font-semibold text-white mb-2 relative z-10">{t(feature.titleKey)}</h3>
                    <p className="text-white/60 text-sm leading-relaxed relative z-10">{t(feature.descKey)}</p>
                  </motion.div>
                )
              })}
            </div>
          ) : (
            /* 2-5 features → horizontal full-width cards with accent bar */
            <div className="max-w-4xl mx-auto space-y-4">
              {product.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                  className="group relative flex gap-6 rounded-2xl bg-white/5 border border-white/10 p-6 md:p-8 backdrop-blur-sm overflow-hidden hover:border-accent/30 transition-colors"
                >
                  {/* Accent bar left */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent/30 group-hover:bg-accent transition-colors" />
                  {/* Ghost number */}
                  <span className="text-[3rem] font-black text-white/[0.06] leading-none flex-shrink-0 select-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-semibold text-white mb-2">{t(feature.titleKey)}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{t(feature.descKey)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── 4. Video (Condizionale) ── */}
      {hasVideo && (
        <section className="bg-surface-light section-padding">
          <div className="container-main">
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto text-center mb-8"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-text-heading">
                {t('products.detail.sections.video')}
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                <iframe
                  src={product.videoUrl}
                  title={`${product.name} video`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── 5. Applicazioni ── */}
      {applications.length > 0 && (
        <section className="relative bg-primary overflow-hidden section-padding">
          {/* Decorative blur */}
          <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl pointer-events-none" />

          <div className="container-main relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left — heading */}
              <motion.div {...fadeInUp} transition={{ duration: 0.5 }}>
                <span className="text-accent text-sm font-semibold uppercase tracking-wider">
                  {t('products.detail.sections.applicationsEyebrow')}
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-4">
                  {t('products.detail.sections.applications')}
                </h2>
                <p className="text-white/60 leading-relaxed">
                  {t('products.detail.sections.applicationsIntro')}
                </p>
              </motion.div>

              {/* Right — pills */}
              <div className="flex flex-wrap gap-3">
                {applications.map((app, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.25, delay: index * 0.03 }}
                    className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-lg hover:border-accent/30 transition-colors"
                  >
                    <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                    <span className="text-sm text-white/80 font-medium">{app}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── 6. Downloads ── */}
      {hasDownloads && (
        <section className="bg-surface-light section-padding">
          <div className="container-main">
            <motion.div {...fadeInUp} transition={{ duration: 0.5 }} className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-text-heading mb-3">
                {t('products.detail.sections.downloads')}
              </h2>
              <p className="text-text-muted max-w-2xl mx-auto">
                {t('products.detail.sections.downloadsIntro')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {product.downloads.map((download, index) => (
                <motion.a
                  key={index}
                  href={download.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                  className="group relative bg-white rounded-xl overflow-hidden border border-surface-border hover:shadow-xl hover:border-accent/30 transition-all p-6 text-center"
                >
                  {/* Gradient bar top */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent to-accent/60" />

                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent/8 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                    <FileText className="w-5 h-5 text-accent" />
                  </div>
                  <p className="font-semibold text-sm text-text-heading group-hover:text-accent transition-colors mb-1">
                    {t(download.labelKey)}
                  </p>
                  <p className="text-xs text-text-muted">PDF</p>

                  {/* Download icon on hover */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Download className="w-4 h-4 text-accent" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 7. Supporto e Garanzia ── */}
      <section className="bg-white section-padding">
        <div className="container-main">
          <motion.h2
            {...fadeInUp}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl font-bold text-text-heading text-center mb-10"
          >
            {t('products.detail.supportWarranty.title')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            {[
              { icon: <Shield className="w-6 h-6" />, title: '5 anni', desc: t('products.detail.supportWarranty.warranty') },
              { icon: <Cpu className="w-6 h-6" />, title: 'Software', desc: t('products.detail.supportWarranty.software') },
              { icon: <Wrench className="w-6 h-6" />, title: 'Firmware', desc: t('products.detail.supportWarranty.firmware') },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.08 }}
                className="group relative bg-white rounded-xl border border-surface-border p-6 text-center hover:shadow-lg hover:border-accent/30 transition-all overflow-hidden"
              >
                {/* Hover accent bar bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center" />

                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center text-primary group-hover:from-accent/15 group-hover:to-accent/5 group-hover:text-accent transition-colors">
                  {item.icon}
                </div>
                <p className="font-bold text-text-heading mb-1.5 text-lg">{item.title}</p>
                <p className="text-sm text-text-muted">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Banner descrittivo */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="max-w-4xl mx-auto bg-surface-light rounded-xl border border-surface-border p-6 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            <p className="text-sm text-text-muted leading-relaxed">{t('products.detail.supportWarranty.desc')}</p>
          </motion.div>
        </div>
      </section>

      {/* ── 8. CTA Banner — Split cinematografico ── */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[450px]">
          {/* Left — category image */}
          <div className="relative min-h-[300px] lg:min-h-0">
            <div className="absolute inset-0 bg-primary/90 flex items-center justify-center">
              <img
                src={product.images[0]}
                alt={product.name}
                className="max-h-[80%] max-w-[80%] object-contain opacity-20"
                loading="lazy"
              />
            </div>
            {/* Fade edge desktop */}
            <div className="hidden lg:block absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-primary to-transparent" />
            {/* Fade bottom mobile */}
            <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary to-transparent" />
          </div>

          {/* Right — content */}
          <div className="relative bg-primary flex items-center">
            {/* Ghost decorative */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 p-10 lg:p-16 max-w-lg">
              <motion.h2
                {...fadeInUp}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-white mb-4"
              >
                {t('products.detail.ctaTitle')}
              </motion.h2>
              <motion.p
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-white/70 leading-relaxed mb-8"
              >
                {t('products.detail.ctaDesc')}
              </motion.p>
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-wrap gap-4"
              >
                <LocalizedLink
                  to={contactPath}
                  className="btn-primary"
                >
                  {t('products.detail.ctaPrimary')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </LocalizedLink>
                {hasDownloads && (
                  <a
                    href={product.downloads[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline border-white text-white hover:bg-white hover:text-primary"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('products.detail.ctaSecondary')}
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. Prodotti Correlati ── */}
      {relatedProducts.length > 0 && (
        <section className="bg-white section-padding">
          <div className="container-main">
            <motion.h2
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-text-heading text-center mb-10"
            >
              {t('products.detail.sections.related')}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((related, index) => (
                <ProductCard
                  key={related.id}
                  product={related}
                  category={category}
                  lang={lang}
                  index={index}
                  showCategory
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
